import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { CategoryService, CategoryPayload } from "@/services/categories.service";
import { Category, CategoryType } from "@/domain/category.interface";

const CATEGORY_TYPES_KEY = "pret-category-types";
const CATEGORY_BY_TYPE_KEY = "pret-category-by-type";

export const useCategoryByType = ({
  type,
  status,
  action,
}: {
  type: string;
  status: string;
  action?: string;
}) => {
  const {
    data: category,
    isLoading: isLoadingCategory,
    refetch: refetchCategory,
  } = useQuery({
    queryKey: ["category", { type }, { action }],
    queryFn: async () => CategoryService.findByType({ type, status }),
  });

  return {
    category,
    isLoadingCategory,
    refetchCategory,
  };
};

export const useCategoryTypes = () => {
  const {
    data: categoryTypes,
    isLoading: isLoadingCategoryTypes,
    refetch: refetchCategoryTypes,
  } = useQuery({
    queryKey: [CATEGORY_TYPES_KEY],
    queryFn: () => CategoryService.getTypes(),
  });

  return {
    categoryTypes,
    isLoadingCategoryTypes,
    refetchCategoryTypes,
  };
};

export const useCategoriesByTypes = (types: CategoryType[]) => {
  const results = useQueries({
    queries: types.map((type) => ({
      queryKey: [CATEGORY_BY_TYPE_KEY, type],
      queryFn: () => CategoryService.findOneByType(type),
    })),
  });

  const categoriesByType = new Map<CategoryType, Category | null>();
  types.forEach((type, idx) => {
    categoriesByType.set(type, (results[idx]?.data as Category | null) ?? null);
  });

  const isLoading = results.some((r) => r.isLoading);
  const isFetching = results.some((r) => r.isFetching);

  return {
    categoriesByType,
    isLoadingCategoriesByTypes: isLoading,
    isFetchingCategoriesByTypes: isFetching,
  };
};

export const useCreateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CategoryPayload) => CategoryService.create(data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [CATEGORY_BY_TYPE_KEY] });
      queryClient.invalidateQueries({ queryKey: ["category", { type: variables.type }] });
    },
  });
};

export const useUpdateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CategoryPayload }) =>
      CategoryService.update({ id, data }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [CATEGORY_BY_TYPE_KEY] });
      queryClient.invalidateQueries({
        queryKey: ["category", { type: variables.data.type }],
      });
    },
  });
};

export const useDeleteCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => CategoryService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CATEGORY_BY_TYPE_KEY] });
      queryClient.invalidateQueries({ queryKey: ["category"] });
    },
  });
};
